/**
 * Portal Login Page
 * Multiple authentication methods for partner portal access
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../services/firebase';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import {
  sendMagicLink,
  sendOTP,
  verifyOTP,
  linkFirebaseToPortal,
  getStoredSession,
  verifySession,
} from '../services/portalService';
import './PortalLogin.css';

const PortalLogin = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [authMethod, setAuthMethod] = useState('magic'); // 'magic', 'otp', 'firebase'
  const [step, setStep] = useState('choose'); // 'choose', 'email', 'otp-verify'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check if user already has a valid session
  useEffect(() => {
    const checkExistingSession = async () => {
      const session = getStoredSession();
      if (session?.sessionToken && session?.accessToken === accessToken) {
        // Verify session is still valid on server
        const result = await verifySession(session.sessionToken);
        if (result.valid) {
          // Already logged in, redirect to portal
          navigate(`/portal/${accessToken}`, { replace: true });
          return;
        }
      }
      setCheckingSession(false);
    };

    checkExistingSession();
  }, [accessToken, navigate]);

  // Check for magic link verification in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get('verified');
    if (verified === 'true') {
      setSuccess('Email verified! Redirecting to portal...');
      setTimeout(() => {
        navigate(`/portal/${accessToken}`, { replace: true });
      }, 1500);
    }
  }, [location, accessToken, navigate]);

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await sendMagicLink(email, accessToken);
      setSuccess(result.message || 'Magic link sent! Check your email.');
      setStep('email-sent');
    } catch (err) {
      setError(err.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await sendOTP(email, accessToken);
      setSuccess(result.message || 'Verification code sent!');
      setStep('otp-verify');
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await verifyOTP(email, accessToken, otp);
      if (result.success) {
        setSuccess('Verified! Redirecting...');
        setTimeout(() => {
          navigate(`/portal/${accessToken}`, { replace: true });
        }, 1000);
      } else {
        setError('Invalid verification code');
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    let popupSignedIn = false;
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      popupSignedIn = true;

      // Link Firebase Auth to portal
      const result = await linkFirebaseToPortal(accessToken);
      if (result.success) {
        setSuccess('Signed in! Redirecting...');
        setTimeout(() => {
          navigate(`/portal/${accessToken}`, { replace: true });
        }, 1000);
      } else {
        setError('Couldn’t link your Google account to this portal. Try a different sign-in method below.');
      }
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User closed popup, not an error
        setLoading(false);
        return;
      }
      // The server rejects Google sign-in when the Google account email
      // doesn't match the portal's registered email. Surface a clear
      // recovery path instead of the raw permission-denied wire message.
      const raw = String(err.message || '');
      const code = String(err.code || '');
      const isEmailMismatch =
        /does not match/i.test(raw) ||
        code === 'functions/permission-denied' ||
        code === 'permission-denied';
      if (popupSignedIn && isEmailMismatch) {
        setError(
          "This portal is registered to a different email address. " +
          "Please use Magic Link or Email Code below with the email on file " +
          "(check the invitation we sent you), or sign in with a Google " +
          "account that matches that email."
        );
      } else {
        setError(raw || 'Failed to sign in with Google. Please try a different method below.');
      }
      // We're signed in to Firebase Auth but not linked to the portal —
      // sign out so a retry starts clean (otherwise the popup may auto-skip
      // and re-trigger the same mismatch silently).
      if (popupSignedIn && auth?.signOut) {
        try { await auth.signOut(); } catch { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const selectMethod = (method) => {
    setAuthMethod(method);
    setStep('email');
    setError(null);
    setSuccess(null);
  };

  if (checkingSession) {
    return (
      <div className="portal-login-container">
        <div className="login-card">
          <div className="login-loading">
            <div className="spinner"></div>
            <p>Checking session...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-logo">FusionICT Billing</h1>
          <p className="login-subtitle">Partner Portal</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {step === 'choose' && (
          <div className="auth-options">
            <h2>Sign in to your portal</h2>
            <p className="auth-intro">Choose how you'd like to sign in:</p>

            <button
              className="auth-option-btn magic-link"
              onClick={() => selectMethod('magic')}
            >
              <span className="auth-option-icon">✉️</span>
              <div className="auth-option-content">
                <span className="auth-option-title">Magic Link</span>
                <span className="auth-option-desc">We'll email you a sign-in link</span>
              </div>
              <span className="recommended-badge">Recommended</span>
            </button>

            <button
              className="auth-option-btn otp"
              onClick={() => selectMethod('otp')}
            >
              <span className="auth-option-icon">🔢</span>
              <div className="auth-option-content">
                <span className="auth-option-title">Email Code</span>
                <span className="auth-option-desc">Get a 6-digit verification code</span>
              </div>
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              className="auth-option-btn google"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={authMethod === 'magic' ? handleSendMagicLink : handleSendOTP} className="email-form">
            <button type="button" className="back-link" onClick={() => setStep('choose')}>
              &larr; Back
            </button>

            <h2>
              {authMethod === 'magic' ? 'Get your magic link' : 'Get verification code'}
            </h2>
            <p className="form-intro">
              Enter the email address associated with your billing account.
            </p>

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading || !email}>
              {loading ? 'Sending...' : authMethod === 'magic' ? 'Send Magic Link' : 'Send Code'}
            </button>
          </form>
        )}

        {step === 'email-sent' && (
          <div className="email-sent">
            <div className="sent-icon">📬</div>
            <h2>Check your email</h2>
            <p>We sent a magic link to <strong>{email}</strong></p>
            <p className="text-small">Click the link in the email to sign in. The link expires in 24 hours.</p>
            <button className="btn-link" onClick={() => setStep('choose')}>
              Use a different method
            </button>
          </div>
        )}

        {step === 'otp-verify' && (
          <form onSubmit={handleVerifyOTP} className="otp-form">
            <button type="button" className="back-link" onClick={() => setStep('email')}>
              &larr; Back
            </button>

            <h2>Enter verification code</h2>
            <p className="form-intro">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>

            <div className="form-group">
              <label htmlFor="otp">Verification code</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
                disabled={loading}
                className="otp-input"
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading || otp.length !== 6}>
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>

            <button
              type="button"
              className="btn-link resend-link"
              onClick={handleSendOTP}
              disabled={loading}
            >
              Didn't receive it? Send again
            </button>
          </form>
        )}

        <footer className="login-footer">
          <p>Secure billing portal by FusionICT</p>
        </footer>
      </div>
    </div>
  );
};

export default PortalLogin;
